import { useState } from 'react';
import { BrandAsset } from '@agency-smm/shared';
import { apiClient } from '../../services/apiClient';
import { convertToWebP } from '../../utils/imageConvert';

export function useAssetManagement(workspaceId: string | undefined) {
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [assetUrl, setAssetUrl] = useState('');
  const [assetName, setAssetName] = useState('');
  const [isAddingAsset, setIsAddingAsset] = useState(false);
  const [assetFiles, setAssetFiles] = useState<File[]>([]);
  const [isUploadingAsset, setIsUploadingAsset] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number } | null>(null);
  const [uploadErrors, setUploadErrors] = useState<string[]>([]);

  const fetchAssets = async () => {
    try {
      const data = await apiClient.get<BrandAsset[]>(`/api/workspaces/${workspaceId}/assets`);
      setAssets(data);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
    }
  };

  const handleAddAsset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assetUrl || !assetName) return;

    setIsAddingAsset(true);
    try {
      const newAsset = await apiClient.post<BrandAsset>(`/api/workspaces/${workspaceId}/assets`, {
        url: assetUrl,
        name: assetName,
      });
      setAssets([...assets, newAsset]);
      setAssetUrl('');
      setAssetName('');
    } catch (error) {
      console.error('Failed to add asset:', error);
    } finally {
      setIsAddingAsset(false);
    }
  };

  const handleDeleteAsset = async (assetId: string) => {
    try {
      await apiClient.delete(`/api/workspaces/${workspaceId}/assets/${assetId}`);
      setAssets(assets.filter((a) => a.id !== assetId));
    } catch (error) {
      console.error('Failed to delete asset:', error);
    }
  };

  const handleSetLogo = async (assetId: string) => {
    try {
      await apiClient.post(`/api/workspaces/${workspaceId}/assets/${assetId}/set-logo`, {});
      setAssets(assets.map((a) => ({ ...a, isLogo: a.id === assetId })));
    } catch (error) {
      console.error('Failed to set logo:', error);
    }
  };

  const handleAssetFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (assetFiles.length === 0) return;

    setIsUploadingAsset(true);
    setUploadProgress({ current: 0, total: assetFiles.length });
    setUploadErrors([]);

    const uploadedAssets: BrandAsset[] = [];
    const errors: string[] = [];
    const BATCH_SIZE = 3;

    const uploadSingleFile = async (file: File): Promise<BrandAsset> => {
      const webpFile = await convertToWebP(file);
      const formData = new FormData();
      formData.append('file', webpFile);
      return apiClient.upload<BrandAsset>(`/api/workspaces/${workspaceId}/assets/upload`, formData);
    };

    try {
      for (let i = 0; i < assetFiles.length; i += BATCH_SIZE) {
        const batch = assetFiles.slice(i, i + BATCH_SIZE);
        const results = await Promise.allSettled(batch.map((file) => uploadSingleFile(file)));

        results.forEach((result, idx) => {
          const file = batch[idx];
          if (result.status === 'fulfilled') {
            uploadedAssets.push(result.value);
          } else {
            console.error(`Failed to upload ${file.name}:`, result.reason);
            errors.push(file.name);
          }
          setUploadProgress({ current: i + idx + 1, total: assetFiles.length });
        });
      }

      if (uploadedAssets.length > 0) {
        setAssets([...assets, ...uploadedAssets]);
      }
      if (errors.length > 0) {
        setUploadErrors(errors);
      }
      setAssetFiles([]);
    } catch (error) {
      console.error('Failed to upload assets:', error);
    } finally {
      setIsUploadingAsset(false);
      setUploadProgress(null);
    }
  };

  const handleAddSelectedImages = async (selectedImages: Set<string>) => {
    if (selectedImages.size === 0) return;

    const imagesToAdd = Array.from(selectedImages);
    const newAssets: BrandAsset[] = [];

    for (let i = 0; i < imagesToAdd.length; i++) {
      const imageUrl = imagesToAdd[i];
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0] || `Image ${i + 1}`;
      const name = fileName.length > 30 ? fileName.substring(0, 30) + '...' : fileName;

      try {
        const newAsset = await apiClient.post<BrandAsset>(`/api/workspaces/${workspaceId}/assets`, {
          url: imageUrl,
          name,
        });
        newAssets.push(newAsset);
      } catch (err) {
        console.error(`Failed to add image: ${imageUrl}`, err);
      }
    }

    if (newAssets.length > 0) {
      setAssets([...assets, ...newAssets]);
    }
  };

  return {
    assets,
    assetUrl,
    setAssetUrl,
    assetName,
    setAssetName,
    isAddingAsset,
    assetFiles,
    setAssetFiles,
    isUploadingAsset,
    uploadProgress,
    uploadErrors,
    fetchAssets,
    handleAddAsset,
    handleDeleteAsset,
    handleSetLogo,
    handleAssetFileUpload,
    handleAddSelectedImages,
  };
}
